<template>
  <ModalDialog
    v-model="visible"
    :title="$localize('CreateNewCaseUploadFolder')"
    :is-wide="true"
    :is-danger="false"
    @open="onOpen"
  >
    <template #default>
      <ItemsLoadingSpinner v-if="state.responseTypesLoading" />
      <div
        v-if="!state.responseTypesLoading"
        class="p-3"
      >
        <div 
          v-if="state.showFormSelector"
          class="row mb-3"
        >
          <div class="col-12">
            <FormTypeSelector
              :label-resource-key="'SelfResponseType'"
              :selected-item="state.selectedResponseType"
              :available-response-types="state.availableResponseTypes"
              :select-item="onResponseTypeSelected"
              :disabled="state.showFormContent"
            />
          </div>
        </div>
        <div v-if="state.showFormContent">
          <div class="row mb-3">
            <div class="col-12">
              <div class="form-group">
                <label
                  for="assignee"
                  class="form-label"
                >{{ $localize('RequestAssignee') }}</label>
                <AssigneeAutoComplete
                  v-model="state.selectedAssignee"
                  :assignees="state.assignees"
                  :set-dropdown-z-index="false"
                  :disabled="false"
                  :placeholder="$localize('UploadFolderAssignedToPlaceholder')"
                />
              </div>
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-12">
              <div class="form-group">
                <label class="form-label">{{ $localize('Priority') }}</label>
                <select
                  v-model="state.selectedPriority"
                  class="form-control"
                >
                  <option :value="SelfResponsePriority.none">
                    {{ $localize('SelfResponsePriorityNone') }}
                  </option>
                  <option
                    :value="SelfResponsePriority.medium"
                    selected
                  >
                    {{ $localize('SelfResponsePriorityMedium') }}
                  </option>
                  <option :value="SelfResponsePriority.high">
                    {{ $localize('SelfResponsePriorityHigh') }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-12">
              <div class="form-group">
                <label class="form-label">{{ $localize('InternalReferenceNumber') }}</label>
                <input
                  v-model="state.internalReferenceNumber"
                  class="form-control"
                  :class="{'has-error': isInternalReferenceNumberExceedsMaxLength}"
                  :placeholder="$localize('InternalReferencePlaceholder')"
                  :aria-invalid="isInternalReferenceNumberExceedsMaxLength"
                >
                <div
                  v-if="isInternalReferenceNumberExceedsMaxLength"
                  class="help-block error-text"
                >
                  {{ $localize('InternalReferenceNumberExceedsMaxLength') }}
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-6 mb-3">
              <div class="form-group">
                <label class="form-label">{{ $localize('RmsId') }}</label>
                <RmsIdSelector
                  ref="rmsIdComponent"
                  v-model="state.selectedRmsId"
                  :disabled="false"
                />
              </div>
            </div>
            <div class="col-6 mb-3">
              <div class="form-group">
                <label class="form-label">{{ $localize('CadIds') }}</label>
                <CadIdsSelector
                  ref="cadIdsComponent"
                  v-model="state.selectedCadIds"
                  :disabled="false"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <DynamicForm
                v-model="state.dynamicResponseFormData"
                class="dynamic-form"
                :form-definition="state.dynamicResponseFormDefinition"
                :is-read-only="false"
                :disable-validation="true"
                @update:model-value="onFormChange"
              />
            </div>
          </div>
        </div>
        <div
          v-if="state.showValidationErrors"
          id="errorValidationMessages"
          class="validation-summary"
          aria-live="assertive"
        >
          <h5><strong>{{ $localize('UploadValidationFailed') }}</strong></h5>
          <ul>
            <li>{{ state.createErrorMessage }}</li>
          </ul>
        </div>
      </div>
    </template>
    <template #footer>
      <button
        ref="closeBtn"
        class="btn btn-outline-secondary"
        @click="visible = false"
      >
        {{ $localize('Cancel') }}
      </button>
      <button
        ref="createBtn"
        class="btn btn-primary"
        :disabled="state.isCreating || state.responseTypesLoading"
        @click="onCreateResponse"
      >
        <i
          v-if="state.isCreating"
          class="fa-regular fa-circle-notch fa-spin me-1"
        />
        {{ $localize('Create') }}
      </button>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
  import SelfResponseModel from '@/Models/selfResponseModel';
  import { ref, computed } from 'vue';
  import DynamicForm from '@/VueComponents/DynamicForm/DynamicForm';
  import ModalDialog from '@/VueComponents/Modals/ModalDialog';
  import RmsIdSelector from '@/VueComponents/RmsIdSelector/RmsIdSelector';
  import CadIdsSelector from '@/VueComponents/CadIdsSelector/CadIdsSelector';
  import businessService from '@/Services/businessService';
  import SelfResponseService from '@/Services/selfResponseService';
  import { useContextDataStore } from '@/VueCore/stores/contextDataStore';
  import logger from '@/Utils/logger';
  import FormTypeSelector from '@/VueComponents/DynamicForm/FormTypeSelector';
  import ResponseFormDefinitionService from '@/Services/responseFormDefinitionService';
  import ItemsLoadingSpinner from '@/VueComponents/Loading/ItemsLoadingSpinner.vue';
  import AssigneeAutoComplete from '@/VueComponents/SharedComponents/AssigneeAutoComplete/AssigneeAutoComplete.vue';
  import { SelfResponsePriority } from '@/Types/Enums/selfResponsePriority';
  import resourceHelper from '@/Utils/resourceHelper';
  import localStorageHelper from '@/Utils/localStorageHelper';
  import { LocalStorageKeyType } from '@/Types/Enums/localStorageKeyType';
  import CreateSelfResponseState from '@/VueComponents/Modals/CreateSelfResponse/types/CreateSelfResponseState';
  import { useRouter } from 'vue-router';
  import AssigneeManager from '@/Utils/assigneeManager';
  import useResetableState from '@/VueCore/utils/useResetableState';

  const router = useRouter();
  const visible = defineModel<boolean>({ default: false });
  const contextData = useContextDataStore();
  
  const { state, resetState } = useResetableState<CreateSelfResponseState>({
    selectedAssignee: null,
    selectedPriority: SelfResponsePriority.medium,
    responseTypesLoading: true,
    internalReferenceNumber: '',
    isCreating: false,
    assignees: [],
    dynamicResponseFormDefinition: '',
    dynamicResponseFormData: {},
    dynamicResponseFormId: '',
    dynamicResponseFormVersion: '',
    dynamicResponseUploadGuidance: '',
    dynamicResponseConfirmationText: '',
    selectedCadIds: [],
    selectedRmsId: '',
    showValidationErrors: false,
    createErrorMessage: '',
    showFormSelector: false,
    showFormContent: false,
    selectedResponseType: null,
    availableResponseTypes: []
  });

  const rmsIdComponent = ref<any>(null);
  const cadIdsComponent = ref<any>(null);

  let isFormValid = true;

  async function onOpen() {
    resetState();
    state.assignees = await businessService.getRequestAssigneeSummaries() ?? [];
    await getResponseFormsTypes();
  }

  function getResponseModel(): SelfResponseModel {
    const assignedGroupId = AssigneeManager.getAssignedGroupId(state.selectedAssignee);
    const assignedPersonaId = AssigneeManager.getAssignedPersonaId(state.selectedAssignee);

    return {
      assignedGroupId: assignedGroupId,
      assignedPersonaId: assignedPersonaId,
      businessId: contextData.userData.business.businessId,
      dynamicResponseFormDefinition: JSON.stringify(state.dynamicResponseFormDefinition),
      dynamicResponseFormData: state.dynamicResponseFormData ? JSON.stringify(state.dynamicResponseFormData) : null,
      internalReferenceNumber: state.internalReferenceNumber,
      referenceNumber: state.internalReferenceNumber,
      creatorPersonaId: contextData.userData.personaId,
      dynamicResponseFormId: state.dynamicResponseFormId,
      dynamicResponseFormVersion: state.dynamicResponseFormVersion,
      dynamicResponseUploadGuidance: state.dynamicResponseUploadGuidance,
      dynamicResponseConfirmationText: state.dynamicResponseConfirmationText,
      responsePriority: state.selectedPriority,
      rmsId: state.selectedRmsId,
      cadIds: state.selectedCadIds
    };
  }

  const isInternalReferenceNumberExceedsMaxLength = computed(() => {
    return state.internalReferenceNumber?.trim().length > 50;
  });

  function isModalValid() {
    return isFormValid && cadIdsComponent.value!.isValid() && rmsIdComponent.value.isValid() && !isInternalReferenceNumberExceedsMaxLength.value;
  }

  function validateAssignee() {
    if (!state.selectedAssignee) {
      return true;
    }

    if (AssigneeManager.groupHasRegisteredContact(state.selectedAssignee)) {
      logger.warning('GroupNeedsRegisteredContact');
      return false;
    }

    return true;
  }

  function onFormChange(data, isValid) {
    isFormValid = isValid;
  }

  async function onCreateResponse() {

    if (!state.selectedResponseType) {
      state.createErrorMessage = resourceHelper.getString('UploadFolderFormNotSelected');
      state.showValidationErrors = true;
      return;
    }

    if (!isModalValid()) {
      return;
    }

    if (!validateAssignee()) {
      return;
    }

    state.showValidationErrors = false;
    state.isCreating = true;

    try {
      const response = await SelfResponseService.createResponse(getResponseModel());
      localStorageHelper.setSessionValue(LocalStorageKeyType.UploadFoldersPage, String(1));
      visible.value = false;
      router.push(`/uploadfolderdetails/${response.id}`);
      logger.success('UploadFolderCreated');
    } catch (error) {
      logger.error('UploadFolderCreationFailed');
    } finally {
      state.isCreating = false;
    }
  }

  async function onResponseTypeSelected(typeId: string) {
    state.selectedResponseType = state.availableResponseTypes.find(x => x.id === typeId)!;
    await getCustomResponseForm();
    state.showFormContent = true;
    state.showValidationErrors = false;
  }

  async function getResponseFormsTypes() {
    const definitions = await ResponseFormDefinitionService.getResponseDefinitions();
    state.availableResponseTypes = definitions;
    if (definitions.length == 1) {
      state.selectedResponseType = definitions[0];
      onResponseTypeSelected(definitions[0].id);
    }
    state.showFormSelector = definitions.length > 1;
    state.responseTypesLoading = false;
  }

  async function getCustomResponseForm() {
    if (state.selectedResponseType) {
      const form = await ResponseFormDefinitionService.getResponseFormById(state.selectedResponseType.id);
      state.dynamicResponseFormDefinition = JSON.parse(form.definition);
      state.dynamicResponseFormId = form.formId;
      state.dynamicResponseFormVersion = form.version;
      state.dynamicResponseUploadGuidance = state.selectedResponseType.uploadGuidance;
      state.dynamicResponseConfirmationText = state.selectedResponseType.confirmationText;
    }
  }
</script>

<style lang="scss" scoped>
@import "sass/site/_colours.scss";

.results-status {
  border: none;
}

.form-control.has-error{
  border-color: $validation-error-color;
  &:focus {
    border-color: $validation-error-color;
    box-shadow: $input-error-box-shadow;
  }
}

.error-text {
    color: $validation-error-color;
    margin-bottom: 0px;
}
</style>
